import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Check } from 'lucide-react';
import { packages } from '../data/mock';
import RegistrationModal from './RegistrationModal';

const Pricing = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <section id="pricing" className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-block mb-4 px-4 py-2 bg-emerald-100 text-emerald-800 rounded-full text-xl font-semibold">
              Pricing
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Perfect Protection Plans for Every Brand
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              From startups to established enterprises, we've designed comprehensive protection packages that grow with your business. Each plan includes expert guidance, priority support, and everything you need to secure your intellectual property with confidence.
            </p>
          </div>

        <Tabs defaultValue="trademark" className="w-full">
          <TabsList className="grid w-full max-w-4xl mx-auto grid-cols-2 md:grid-cols-5 h-auto mb-12">
            <TabsTrigger value="trademark" className="text-xl md:text-base py-3">Trademark</TabsTrigger>
            <TabsTrigger value="copyright" className="text-xl md:text-base py-3">Copyright</TabsTrigger>
            <TabsTrigger value="formation" className="text-xl md:text-base py-3">Formation</TabsTrigger>
            <TabsTrigger value="search" className="text-xl md:text-base py-3">Search</TabsTrigger>
            <TabsTrigger value="amazon" className="text-xl md:text-base py-3">Amazon</TabsTrigger>
          </TabsList>

          <TabsContent value="trademark">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12 mb-8">
              {packages.trademark.map((pkg, index) => (
                <Card key={index} className={`relative transition-all duration-300 hover:shadow-xl flex flex-col ${pkg.featured ? 'border-2 border-emerald-500 shadow-lg' : 'hover:border-emerald-200'}`}>
                  {pkg.featured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-3 py-1 rounded-full text-xl font-semibold whitespace-nowrap z-10">
                      Most Popular
                    </div>
                  )}
                  <CardHeader className="pb-3">
                    <CardTitle className="text-xl">{pkg.name}</CardTitle>
                    <CardDescription className="text-2xl font-bold text-gray-900">
                      ${pkg.price}<span className="text-xl text-gray-500">*</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1 pb-3">
                    <div className="max-h-[240px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-emerald-300 scrollbar-track-gray-100">
                      <ul className="feature-list space-y-0">
                        {pkg.features.map((feature, idx) => (
                          <li key={idx}>
                            <Check className="w-4 h-4 text-emerald-600" />
                            <span className="text-base text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <p className="text-xl text-gray-500 mt-3">*(Govt. filing fee not included)</p>
                  </CardContent>
                  <CardFooter className="pt-3">
                    <Button onClick={() => setIsModalOpen(true)} className={`w-full h-10 text-xl ${pkg.featured ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800' : 'bg-gray-900 hover:bg-gray-800'}`}>
                      Register My Trademark
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="copyright">
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mt-12 mb-8">
              {packages.copyright.map((pkg, index) => (
                <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:border-emerald-200 flex flex-col">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-xl">{pkg.name}</CardTitle>
                    <CardDescription className="text-2xl font-bold text-gray-900">
                      ${pkg.price}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1 pb-3">
                    <div className="max-h-[240px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-emerald-300 scrollbar-track-gray-100">
                      <ul className="feature-list space-y-0">
                        {pkg.features.map((feature, idx) => (
                          <li key={idx}>
                            <Check className="w-4 h-4 text-emerald-600" />
                            <span className="text-base text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                  <CardFooter className="pt-3">
                    <Button onClick={() => setIsModalOpen(true)} className="w-full h-10 text-xl bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800">
                      Register My Copyright
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="formation">
            <div className="mt-12 mb-8">
              <Card className="max-w-2xl mx-auto hover:shadow-xl transition-all duration-300 flex flex-col">
              <CardHeader className="pb-3">
                <CardTitle className="text-2xl">START YOUR BUSINESS WITH EASE</CardTitle>
              </CardHeader>
              <CardContent className="flex-1 pb-3">
                <ul className="feature-list max-h-[240px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-emerald-300 scrollbar-track-gray-100">
                  <li>
                    <Check className="w-5 h-5 text-emerald-600" />
                    <p className="text-base text-gray-700">Make sure you're not liable for any business debts. Ensure you're not on the hook for business liabilities</p>
                  </li>
                  <li>
                    <Check className="w-5 h-5 text-emerald-600" />
                    <p className="text-base text-gray-700">Company Formation through a Simple Process: Our 3 step process includes entering your details, selecting a service, and payment</p>
                  </li>
                  <li>
                    <Check className="w-5 h-5 text-emerald-600" />
                    <p className="text-base text-gray-700">Save Money on Taxes: For potential savings you get to decide whether you're taxed as an LLC or a Corporation</p>
                  </li>
                </ul>
              </CardContent>
              <CardFooter className="pt-3">
                <Button onClick={() => setIsModalOpen(true)} className="w-full h-10 text-xl bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800">
                  Start Company Formation
                </Button>
              </CardFooter>
            </Card>
            </div>
          </TabsContent>

          <TabsContent value="search">
            <div className="grid md:grid-cols-3 gap-6 mt-12 mb-8">
              {packages.search.map((pkg, index) => (
                <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:border-emerald-200 flex flex-col">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-xl">{pkg.name}</CardTitle>
                    <CardDescription className="text-2xl font-bold text-gray-900">
                      ${pkg.price}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1 pb-3">
                    <div className="max-h-[240px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-emerald-300 scrollbar-track-gray-100">
                      <ul className="feature-list space-y-0">
                        {pkg.features.map((feature, idx) => (
                          <li key={idx}>
                            <Check className="w-4 h-4 text-emerald-600" />
                            <span className="text-base text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                  <CardFooter className="pt-3">
                    <Button onClick={() => setIsModalOpen(true)} className="w-full h-10 text-xl bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800">
                      Start Trademark Search
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="amazon">
            <div className="grid md:grid-cols-3 gap-6 mt-12 mb-8">
              {packages.amazon.map((pkg, index) => (
                <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:border-emerald-200 flex flex-col">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-xl">{pkg.name}</CardTitle>
                    <CardDescription className="text-2xl font-bold text-gray-900">
                      Starting at ${pkg.price}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1 pb-3">
                    <div className="max-h-[240px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-emerald-300 scrollbar-track-gray-100">
                      <ul className="feature-list space-y-0">
                        {pkg.features.map((feature, idx) => (
                          <li key={idx}>
                            <Check className="w-4 h-4 text-emerald-600" />
                            <span className="text-base text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                  <CardFooter className="pt-3">
                    <Button onClick={() => setIsModalOpen(true)} className="w-full h-10 text-xl bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800">
                      Start Amazon Brand Registry
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
    <RegistrationModal open={isModalOpen} onOpenChange={setIsModalOpen} />
  </>
  );
};

export default Pricing;